import * as Dialog from '@radix-ui/react-dialog';
import { AsterMark, Button, Panel } from '@head-of-trading/ui';
import { type FormEvent, useState } from 'react';
import type { AccountView } from '@head-of-trading/contracts';
import { LanguageSwitcher, useLanguage } from '../i18n/context.js';
import { screensCopy } from '../i18n/screens.js';

interface LoginProps {
  readonly busy: boolean;
  readonly error: string | null;
  readonly takeover: AccountView | null;
  readonly onSubmit: (pseudo: string) => Promise<void>;
  readonly onTakeover: () => Promise<void>;
  readonly onCancelTakeover: () => void;
}

export function Login({ busy, error, takeover, onSubmit, onTakeover, onCancelTakeover }: LoginProps) {
  const { language } = useLanguage();
  const copy = screensCopy[language].access;
  const [pseudo, setPseudo] = useState('');
  const [localError, setLocalError] = useState<'length' | 'characters' | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const clean = pseudo.trim().replace(/\s+/g, ' ');
    if (clean.length < 3 || clean.length > 24) { setLocalError('length'); return; }
    if (!/^[\p{L}\p{N}_ -]+$/u.test(clean)) { setLocalError('characters'); return; }
    setLocalError(null);
    void onSubmit(clean);
  };

  return (
    <main className="access-screen">
      <div className="access-screen__backdrop" aria-hidden="true" />
      <section className="access-screen__brand">
        <AsterMark className="access-screen__logo" />
        <p>{copy.brandStatement}</p>
        <div className="access-screen__tape" aria-hidden="true"><span>{copy.tapeNq}</span><span>{copy.tapeEs}</span><span>{copy.tapeZq}</span></div>
      </section>
      <Panel className="access-card" eyebrow={copy.panelEyebrow} title={copy.panelTitle} tools={<LanguageSwitcher />}>
        <p>{copy.panelDescription}</p>
        <form onSubmit={submit} noValidate>
          <label htmlFor="pseudo">{copy.pseudonymLabel}</label>
          <input data-testid="pseudo-input" id="pseudo" name="pseudo" autoFocus autoComplete="username" value={pseudo} onChange={(event) => setPseudo(event.target.value)} maxLength={24} placeholder={copy.pseudonymPlaceholder} />
          {(localError || error) && <p className="form-error" role="alert">{localError === 'length' ? copy.pseudonymLengthError : localError === 'characters' ? copy.pseudonymCharactersError : error}</p>}
          <Button data-testid="pseudo-submit" variant="primary" size="lg" type="submit" disabled={busy}>{busy ? copy.opening : copy.continue}</Button>
        </form>
        <small>{copy.progressSaved} · {copy.simulatedData} · <a href={`${import.meta.env.BASE_URL}privacy`}>{copy.privacy}</a></small>
      </Panel>

      <Dialog.Root open={takeover !== null} onOpenChange={(open) => { if (!open) onCancelTakeover(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog dialog--compact">
            <Dialog.Title>{copy.takeoverTitle}</Dialog.Title>
            <Dialog.Description>{copy.takeoverBeforeAccount}<strong>{takeover?.displayName}</strong>{copy.takeoverAfterAccount}</Dialog.Description>
            <div className="dialog-actions"><Button variant="quiet" onClick={onCancelTakeover}>{copy.cancel}</Button><Button variant="primary" onClick={() => void onTakeover()}>{copy.takeoverHere}</Button></div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </main>
  );
}
